import React, { memo, useCallback, useEffect, useMemo, useState } from "react";

import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import { useForm } from "react-hook-form";

import styled from "@emotion/styled";

import { Button } from "@/components/Buttons";
import Guide from "@/components/Guide";
import Table from "@/components/Table";

import CommentsModal from "@/modals/CommentsModal";
import CsListModal from "@/modals/CsListModal";
import DeliveryModal from "@/modals/DeliveryModal";

import { COMMENT_MENU_TYPE } from "@/stores/DeliveryComments";
import { QUERY_SORT } from "@/stores/SentBackDeliveries";
import {
  useLoadingStore,
  useModalStore,
  useSentBackDeliveriesStore,
} from "@/stores/hooks";

const BodyContent = styled.div``;
const Container = styled.div`
  width: 100%;
  padding-left: 20px;
  padding-right: 20px;
  padding-bottom: 40px;
`;

const SearchWrap = styled.div`
  padding-top: 15px;
`;
const SearchRow = styled(Row)`
  margin-bottom: 10px;
`;
const Column = styled(Col)`
  display: flex;
  align-items: center;
`;
const ColumnRight = styled(Column)`
  justify-content: flex-end;
`;
const SearchButton = styled(Button)`
  margin-left: 5px;
`;
const ActionButton = styled(Button)`
  margin-left: 4px;
  margin-bottom: 4px;
`;
const FormControl = styled(Form.Control)`
  width: ${({ width }) => width || 180}px !important;
`;
const Wave = styled.span`
  margin: 0 5px;
`;
const SearchLabel = styled.div`
  font-weight: bold;
  margin-right: 10px;
`;
const TabWrap = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
`;
const TabLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 4px;
  margin: 0;
  font-weight: ${({ active }) => (active ? "bold" : "normal")};
  cursor: pointer;
`;
const TableWrap = styled.div``;
const ColumnLink = styled.div`
  cursor: pointer;
  text-decoration: underline;
`;

const SORT_DATE = "date";

const SORT_OPTIONS = [
  { value: SORT_DATE, label: "반송완료일자" },
  { value: QUERY_SORT.UNCONFIRMED_COMMENT, label: "미확인코멘트" },
  { value: QUERY_SORT.COMMENT_CREATED_AT, label: "코멘트" },
];

const FILTER_TABS = [{ id: "all", label: "전체" }];

const getSortDropdownValue = (sort) => {
  if (sort === QUERY_SORT.UNCONFIRMED_COMMENT) {
    return QUERY_SORT.UNCONFIRMED_COMMENT;
  }

  if (sort === QUERY_SORT.COMMENT_CREATED_AT) {
    return QUERY_SORT.COMMENT_CREATED_AT;
  }

  return SORT_DATE;
};

const mapSearchKeyword = (value) => {
  const keyword = (value || "").trim();

  if (!keyword) {
    return { bookId: "", receiverName: "", receiverMobile: "" };
  }

  const digits = keyword.replace(/[-\s]/g, "");

  if (/^01\d{8,9}$/.test(digits) || /^0\d{9,10}$/.test(digits)) {
    return { bookId: "", receiverName: "", receiverMobile: keyword };
  }

  if (/^\d+$/.test(digits) && digits.length >= 8) {
    return { bookId: keyword, receiverName: "", receiverMobile: "" };
  }

  return { bookId: "", receiverName: keyword, receiverMobile: "" };
};

const BookIdColumn = memo(({ bookId, onAfterSubmit }) => {
  const { openModal } = useModalStore();

  const handleClick = useCallback(async () => {
    if (!bookId) {
      return;
    }

    openModal(
      <DeliveryModal
        key={`sent-back-delivery-${bookId}-modal`}
        bookId={bookId}
        onAfterSubmit={onAfterSubmit}
      />,
    );
  }, [bookId, onAfterSubmit, openModal]);

  return <ColumnLink onClick={handleClick}>{bookId}</ColumnLink>;
});

const ActionsColumn = memo(({ row }) => {
  const { openModal } = useModalStore();

  const handleOpenCsList = useCallback(() => {
    const bookId = row.renderBookId();

    if (!bookId) {
      window.alert("송장번호가 없어 CS를 조회할 수 없습니다.");
      return;
    }

    openModal(
      <CsListModal key={`sent-back-cs-list-${bookId}`} bookId={bookId} />,
    );
  }, [openModal, row]);

  const handleOpenComments = useCallback(() => {
    const bookId = row.renderBookId();

    if (!bookId) {
      window.alert("송장번호가 없어 코멘트를 조회할 수 없습니다.");
      return;
    }

    openModal(
      <CommentsModal
        key={`sent-back-comments-${bookId}`}
        bookId={bookId}
        menuType={COMMENT_MENU_TYPE.SENT_BACK}
      />,
    );
  }, [openModal, row]);

  return (
    <div>
      <ActionButton type="button" width="70px" onClick={handleOpenCsList}>
        CS보기
      </ActionButton>
      <ActionButton type="button" width="70px" onClick={handleOpenComments}>
        코멘트
      </ActionButton>
    </div>
  );
});

export default function SentBackDeliveries() {
  const { state, ...actions } = useSentBackDeliveriesStore();
  const { startLoading, finishLoading } = useLoadingStore();
  const { handleSubmit } = useForm();
  const [keyword, setKeyword] = useState("");

  const fetchAll = useCallback(
    async (queryOverride) => {
      try {
        startLoading();
        await actions.fetchAll(queryOverride);
      } catch (e) {
        window.alert(`반송완료 내역 조회에 실패했습니다.\n${e.message}`);
      }

      finishLoading();
    },
    [actions.fetchAll, finishLoading, startLoading],
  );

  useEffect(() => {
    fetchAll();
  }, [state.query.page, state.query.pageSize, state.query.sort]);

  const handleChangeDate = useCallback(
    (e) => {
      actions.setQuery({ [e.target.name]: e.target.value });

      if (
        e.target.name === "sentBackAtFrom" &&
        e.target.value > state.query.sentBackAtTo
      ) {
        actions.setQuery({ sentBackAtTo: e.target.value });
      }

      if (
        e.target.name === "sentBackAtTo" &&
        e.target.value < state.query.sentBackAtFrom
      ) {
        actions.setQuery({ sentBackAtFrom: e.target.value });
      }
    },
    [actions.setQuery, state.query],
  );

  const handleChangeKeyword = useCallback((e) => {
    setKeyword(e.target.value);
  }, []);

  const onSubmit = useCallback(() => {
    const nextQuery = {
      ...state.query,
      page: 1,
      ...mapSearchKeyword(keyword),
    };

    actions.setQuery(nextQuery);
    fetchAll(nextQuery);
  }, [actions.setQuery, fetchAll, keyword, state.query]);

  const handleChangeSort = useCallback(
    (e) => {
      const value = e.target.value;
      const sort = value === SORT_DATE ? QUERY_SORT.SENT_BACK_AT : value;

      actions.setQuery({
        page: 1,
        sort,
      });
    },
    [actions.setQuery],
  );

  const handleGoToPage = useCallback(
    (page) => {
      actions.setQuery({ page });
    },
    [actions.setQuery],
  );

  const handleNextPage = useCallback(() => {
    actions.setQuery({ page: state.query.page + 1 });
  }, [actions.setQuery, state.query.page]);

  const handlePreviousPage = useCallback(() => {
    actions.setQuery({ page: state.query.page - 1 });
  }, [actions.setQuery, state.query.page]);

  const handleSelect = useCallback(() => {}, []);

  const columns = useMemo(
    () => [
      {
        Header: "배송상태",
        accessor: (row) => row.renderDeliveryStatus(),
        width: 90,
      },
      {
        Header: "송장번호",
        accessor: (row) => (
          <BookIdColumn bookId={row.renderBookId()} onAfterSubmit={fetchAll} />
        ),
        width: 110,
      },
      {
        Header: "주문번호",
        accessor: (row) => row.renderOrderIdFromCorp(),
        width: 130,
      },
      {
        Header: "주문번호서브",
        accessor: (row) => row.renderOrderIdFromCorpSub(),
        width: 90,
      },
      {
        Header: "반송사유",
        accessor: (row) => row.renderSentBackReason(),
        width: 140,
      },
      {
        Header: "반송완료일시",
        accessor: (row) => row.renderSentBackAt(),
        width: 140,
      },
      {
        Header: "최근코멘트작성자",
        accessor: (row) => row.renderLatestCommentWriterName(),
        width: 120,
      },
      {
        Header: "최근코멘트작성일시",
        accessor: (row) => row.renderLatestCommentCreatedAt(),
        width: 140,
      },
      {
        Header: "코멘트확인여부",
        accessor: (row) => row.renderCommentConfirmed(),
        width: 110,
      },
      {
        Header: "처리",
        accessor: (row) => (
          <ActionsColumn row={row} />
        ),
        width: 90,
      },
    ],
    [fetchAll],
  );

  return (
    <BodyContent>
      <Guide />
      <Container>
        <SearchWrap>
          <SearchRow>
            <Column xs={8}>
              <Form inline onSubmit={handleSubmit(onSubmit)}>
                <SearchLabel>반송완료일자</SearchLabel>
                <FormControl
                  name="sentBackAtFrom"
                  onChange={handleChangeDate}
                  type="date"
                  value={state.query.sentBackAtFrom}
                />
                <Wave>~</Wave>
                <FormControl
                  name="sentBackAtTo"
                  onChange={handleChangeDate}
                  type="date"
                  value={state.query.sentBackAtTo}
                />
                <SearchButton type="submit" width="100px">
                  조회
                </SearchButton>
              </Form>
            </Column>
            <ColumnRight xs={4}>
              <Form inline onSubmit={handleSubmit(onSubmit)}>
                <FormControl
                  width={260}
                  name="keyword"
                  onChange={handleChangeKeyword}
                  placeholder="송장번호, 수하인명, 연락처 조회"
                  value={keyword}
                />
                <SearchButton type="submit" width="100px">
                  조회
                </SearchButton>
              </Form>
            </ColumnRight>
          </SearchRow>
          <SearchRow>
            <Column xs={8}>
              <TabWrap>
                {FILTER_TABS.map((tab) => (
                  <TabLabel key={tab.id} active>
                    <input
                      checked
                      name="sent-back-filter-tab"
                      readOnly
                      type="radio"
                    />
                    {tab.label}
                  </TabLabel>
                ))}
              </TabWrap>
            </Column>
            <ColumnRight xs={4}>
              <SearchLabel>전체 총 {state.totalCount} 조회</SearchLabel>
              <FormControl
                width={140}
                as="select"
                name="sort"
                onChange={handleChangeSort}
                value={getSortDropdownValue(state.query.sort)}
                custom
              >
                {SORT_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </FormControl>
            </ColumnRight>
          </SearchRow>
        </SearchWrap>
        <TableWrap>
          <Table
            responsive
            bordered
            hover
            columns={columns}
            currentPage={state.query.page}
            data={state.rows}
            goToPage={handleGoToPage}
            nextPage={handleNextPage}
            onSelect={handleSelect}
            pageCount={state.pageCount}
            pageSize={state.query.pageSize}
            previousPage={handlePreviousPage}
            showPageSize={false}
          />
        </TableWrap>
      </Container>
    </BodyContent>
  );
}
